<template>
    <div v-if="isSearchMode" class="search-bar">
        <b-input-group>
            <button
                v-b-tooltip.top
                title="Close search"
                class="close-search"
                @click="closeSearch"
            >
                <i class="fa fa-times" />
            </button>
            <b-form-input
                type="search"
                class="form-control"
                placeholder="Search notifications"
                autofocus
                v-model="searchValue"
                @input="handleSearch"
                ref="inputSearch"
            />
            <b-input-group-append class="append">
                <i class="fa fa-search" />
            </b-input-group-append>

            <b-input-group-append class="dismiss-wrap">
                <button-dismiss-all
                    @click="$emit('dismiss-all')"
                    :loading="isDismissingAll"
                    :checkedItems="checkedItems"
                    :disabled="disableDismissAll"
                />
            </b-input-group-append>
        </b-input-group>
        <div class="filter-applied" v-if="selectedFilters.length > 0">
            <span>Search within:</span> {{ filterAppliedLabel }} (<a
                href="#"
                @click.prevent="$emit('clear-filter-search')"
                >Clear</a
            >)
        </div>
    </div>
    <div v-else class="filter-search">
        <div class="checkbox" style="width: 15px; padding-top: 5px;">
            <input
                id="app-notification-input-check-all"
                type="checkbox"
                :checked="checkedAll"
                @change="$emit('check-all', $event.target.checked)"
            />
        </div>
        <div class="filter">
            <b-dropdown
                text="Filter by type"
                variant="link"
                toggle-class="btn-filter"
            >
                <div
                    v-for="item in filterTypes"
                    :key="item.id"
                    class="filter-item"
                >
                    <b-form-checkbox
                        plain
                        class="cb-wrapper"
                        :checked="selectedFilters.includes(item.type)"
                        @change="$emit('select-filter-type', item.type)"
                    >
                        <i :class="`fa ${icons[item.type]}`" />
                        {{ item.label }}
                        <span class="count-value">({{ item.count }})</span>
                    </b-form-checkbox>
                </div>
                <a
                    v-if="selectedFilters.length > 0"
                    href="#"
                    @click.prevent.stop="$emit('clear-filter')"
                    class="clearAll"
                >
                    Clear all
                </a>
            </b-dropdown>
            <a
                v-if="selectedFilters.length > 0"
                href="#"
                class="clear"
                @click.prevent.stop="$emit('clear-filter')"
            >
                Clear
            </a>
        </div>
        <div class="right">
            <button class="search" @click="searchModeOn">
                <i class="fa fa-search" />
            </button>

            <button-dismiss-all
                @click="$emit('dismiss-all')"
                :loading="isDismissingAll"
                :disabled="isFetching || disableDismissAll"
                :checkedItems="checkedItems"
            />
        </div>
    </div>
</template>

<script>
import { debounce } from "lodash";
import ButtonDismissAll from "./button-dismiss-all";

export default {
    components: {
        ButtonDismissAll
    },
    props: {
        isFetching: {
            type: Boolean,
            default: false
        },
        filterTypes: {
            type: Array
        },
        selectedFilters: {
            type: Array
        },
        icons: {
            type: Object
        },
        isDismissingAll: {
            type: Boolean,
            default: false
        },
        currentListCount: {
            type: Number,
            default: 0
        },
        isSearchMode: {
            type: Boolean,
            default: false
        },
        checkedAll: {
            type: Boolean
        },
        checkedItems: {
            type: Array
        },
        isDisableDismiss: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            searchValue: ""
        };
    },
    computed: {
        disableDismissAll() {
            if (this.isFetching) {
                return true;
            }
            if (this.isDisableDismiss) {
                return true;
            }
            return false;
        },
        filterAppliedLabel() {
            let arr = [];
            for (let type of this.selectedFilters) {
                let filter = this.filterTypes.find(f => f.type === type);
                if (filter) {
                    arr.push(filter.label);
                }
            }
            return arr.join(", ");
        }
    },
    methods: {
        handleSearch() {
            if (this.searchValue.length > 2) {
                this.search(this.searchValue, this);
            } else if (this.searchValue === "") {
                this.$emit("clear-search-query");
            }
        },
        search: debounce(async (query, self) => {
            self.$emit("search", query);
        }, 350),
        closeSearch() {
            this.searchValue = "";
            this.$emit("close-search");
        },
        searchModeOn() {
            this.$emit("start-search-mode");
        },
        focusInputSearch() {
            this.$refs.inputSearch.focus();
        }
    }
};
</script>

<style lang="scss" scoped>
.search-bar {
    padding: 10px 20px;

    input.form-control {
        border-color: #1e3050;
        border-top-left-radius: 3px;
        border-bottom-left-radius: 3px;
    }

    .append {
        display: flex;
        justify-content: center;
        align-items: center;
        background: #1e3050;
        padding: 0 10px;
        color: white;
        border-top-right-radius: 3px;
        border-bottom-right-radius: 3px;
    }

    button.dismiss-all,
    button.close-search {
        background: transparent;
        border: none;
        outline: none;
    }

    button.close-search {
        padding-right: 10px;
    }

    .dismiss-wrap {
        align-items: center;
    }

    .filter-applied {
        padding: 7px 26px;
        font-size: 0.9em;

        span {
            font-weight: bold;
        }
    }
}

.filter-search {
    padding: 10px 20px 5px 20px;
    display: flex;
    align-items: center;
    gap: 5px;

    .filter {
        display: flex;
        align-items: center;
        column-gap: 10px;

        a.clear {
            font-size: 0.9em;
        }

        .filter-item {
            white-space: nowrap;
            padding: 5px 12px;

            ::v-deep label {
                display: block;
                cursor: pointer;
                user-select: none;
            }

            .cb-wrapper {
                display: flex;
                align-items: center;

                ::v-deep input {
                    margin-top: 0;
                }

                i.fa {
                    width: 20px;
                    text-align: center;
                }
            }
        }

        ::v-deep .btn-filter {
            color: inherit;
            font-size: 1em;
            padding: 0;
            margin: 0;
        }
    }

    .right {
        flex: 1;
        display: flex;
        justify-content: flex-end;
        align-items: flex-start;

        button {
            background: transparent;
            border: none;
            outline: none;

            &.search {
                margin-right: 7px;
            }
        }
    }

    a.clearAll {
        display: block;
        padding: 5px 12px;
        font-weight: normal;
    }
}
</style>
